import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AfterViewInit, Component, Inject, NgZone, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { TranslocoCurrencyPipe, TranslocoDatePipe, TranslocoDecimalPipe, TranslocoPercentPipe } from '@jsverse/transloco-locale';
import { MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DatePickerModule } from 'primeng/datepicker';
import { DialogModule } from 'primeng/dialog';
import { FloatLabelModule } from 'primeng/floatlabel';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { MenuModule } from 'primeng/menu';
import { MultiSelectModule } from 'primeng/multiselect';
import { RippleModule } from 'primeng/ripple';
import { SelectModule } from 'primeng/select';
import { SkeletonModule } from 'primeng/skeleton';
import { TableModule } from 'primeng/table';
import { TextareaModule } from 'primeng/textarea';
import { BehaviorSubject, debounceTime, filter, Subject, takeUntil } from 'rxjs';
import { LongPressDirective } from '../../directives/long-press.directive';
import { Column } from '../../interfaces/table';
import { getDefaultUserReport, UserReport, UserReportFilter } from '../../interfaces/userReport.interface';
import { DurationPipe } from '../../pipes/duration.pipe';
import { ConfirmDialogsService } from '../../services/confirm-dialogs.service';
import { NotificationsService } from '../../services/notifications.service';
import { WebsocketService } from '../../services/websocket.service';
import { getPathKey } from '../../utilities/util';


type MAIN_I = UserReport
type FILTER_I = UserReportFilter

@Component({
  selector: 'app-admin-user-report-view',
  imports: [
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    RippleModule,
    InputTextModule,
    TranslocoDirective,
    DialogModule,
    FloatLabelModule,
    InputNumberModule,
    MenuModule,
    MultiSelectModule,
    SelectModule,
    TextareaModule,
    SkeletonModule,
    DatePickerModule,
    LongPressDirective
  ],
  providers: [TranslocoDatePipe, TranslocoCurrencyPipe, TranslocoDecimalPipe, TranslocoPercentPipe, DurationPipe],
  templateUrl: './admin-user-report-view.component.html',
  styleUrl: './admin-user-report-view.component.scss'
})
export class AdminUserReportViewComponent implements OnDestroy, AfterViewInit {
  //^ --------------------------------------------------------------------------------------
  //^ ---------------------- CONFIGURAZIONE BASE -------------------------------------------
  //^ --------------------------------------------------------------------------------------

  //? TRADUZIONI
  TITLE = "userReports"
  SEARCH_PLACEHOLDER = "searchUserReports"
  FILTERS_TITLE = "filterUserReports"
  DELETE_CONFIRMATION_MESSAGE = "deleteUserReportConfirm"

  //? DATI
  KEY_NAME = 'reportId'
  DEFAULT_DATA_FN = getDefaultUserReport
  GET_DATA_ENDPOINT = "getUserReports"
  GET_RECORD_ENDPOINT = "getUserReport"
  GET_FILTERS_ENDPOINT = "getUserReportsFilters"
  DELETE_DATA_ENDPOINT = "deleteUserReports"

  //! configurare columns, dataFilters e _batchMenuStructure 

  //^ --------------------------------------------------------------------------------------
  //^ ---------------------- INIZIALIZZAZIONI ----------------------------------------------
  //^ --------------------------------------------------------------------------------------

  //? DATI E TABELLE
  columns: Column[] = [
    { field: 'reportId', header: 'id', visible: false },
    { field: 'section', header: 'section', visible: true, pipe: "translate" },
    { field: 'category', header: 'category', visible: true, pipe: ["translate", { prefix: "userReports." }] },
    { field: 'details', header: 'details', visible: true, pipe: ['custom', (v: any) => { return v.length >= 50 ? v.substring(0, 50) + '...' : v }] },
    { field: 'userIdDetails.username', header: 'username', visible: true },
    { field: 'creationTimestamp', header: 'timestamp', visible: true, pipe: ['date', { dateStyle: "short", timeStyle: "short" }] },
  ]
  data: MAIN_I[] = []

  get dataFilterFields() {
    return this.columns.filter(column => column.visible).map(column => column.field);
  }

  selectedRows: any[] = []

  dataLoading$ = new BehaviorSubject<boolean>(false)
  dataLoadingUI$ = this.dataLoading$.pipe(
    debounceTime(200)
  );

  _dataTrackBy: (index: number, item: any) => any = (index: number, item: any) => {
    return item.KEY_NAME
  };

  //? VIEW
  formDialogVisible: boolean = false;
  filtersDialogVisible: boolean = false;
  formDialogData: MAIN_I = this.DEFAULT_DATA_FN();

  dataFilters: FILTER_I = {
    options: {
      section: undefined
    },
    cache: {
    },
    values: {},
    applied: {}
  };
  get appliedFiltersCount() {
    let cnt = Object.values(this.dataFilters.applied).filter(value => value !== undefined && value !== null).length
    return cnt == 0 ? undefined : String(cnt)
  }

  get isNewRecordDialog() {
    return this.formDialogData[(this.KEY_NAME as keyof MAIN_I)] === ''
  }

  _batchMenuStructure: MenuItem[] = [
    {
      label: 'batchOperations',
      items: [
        {
          label: 'deleteSelected',
          icon: 'pi pi-trash',
          command: () => {
            this.deleteRecords()
          }
        }
      ]
    }
  ]

  batchMenu: MenuItem[] = []

  //? ALTRO
  destroy$ = new Subject<void>();

  //^ --------------------------------------------------------------------------------------
  //^ ---------------------- LIFE CYCLE - EVENTI -------------------------------------------
  //^ --------------------------------------------------------------------------------------

  constructor(
    private zone: NgZone,
    private http: HttpClient,
    private confirmDialogs: ConfirmDialogsService,
    private notifications: NotificationsService,
    private transloco: TranslocoService,
    private translocoDate: TranslocoDatePipe,
    private translocoCurrency: TranslocoCurrencyPipe,
    private translocoDecimal: TranslocoDecimalPipe,
    private translocoPercent: TranslocoPercentPipe,
    private durationPipe: DurationPipe,
    private wss: WebsocketService,
    @Inject('ENVIRONMENT') private ENV: any
  ) {
    transloco.events$.pipe(
      takeUntil(this.destroy$),
      filter((event) => event.type === "translationLoadSuccess")
    ).subscribe((e) => {
      this.batchMenu = this.translateObjectValues(this._batchMenuStructure, 'label')
    });

    wss.eventsSubject$.pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (e) => {
        if (e.type == "REPORT_NOTIFICATION") {
          this.refreshData()
        }
      }
    })

    this.refreshData()
  }


  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    this.transloco.langChanges$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.batchMenu = this.translateObjectValues(this._batchMenuStructure, 'label')
    });
  }

  //^ --------------------------------------------------------------------------------------
  //^ ---------------------- ALTRO ---------------------------------------------------------
  //^ --------------------------------------------------------------------------------------

  //? DATI E TABELLE
  refreshData() {
    this.dataLoading$.next(true)
    this.http.post(this.ENV.apiUrl + this.GET_DATA_ENDPOINT, {
      filters: this.dataFilters.values
    }).pipe(takeUntil(this.destroy$)).subscribe({
      next: (resp: any) => {
        this.dataFilters.applied = structuredClone(this.dataFilters.values)
        this.data = resp.data;
        this.selectedRows = []
        this.dataLoading$.next(false)
        // this.http.post(this.ENV.apiUrl + this.GET_FILTERS_ENDPOINT, {}).subscribe({
        //   next: (resp: any) => {
        //     this.dataFilters.options = resp?.data;
        //   }
        // })
      },
      error: () => {
        this.dataLoading$.next(false)
      }
    })
  }

  openFilters() {
    this.filtersDialogVisible = true;
  }

  toggleFilters() {
    this.filtersDialogVisible = !this.filtersDialogVisible;
  }

  hideFilters() {
    this.filtersDialogVisible = false;
  }

  openFormDialog(record?: MAIN_I) {
    if (record) {
      this.dataLoading$.next(true)
      this.http.post(this.ENV.apiUrl + this.GET_RECORD_ENDPOINT, { ...record }).pipe(takeUntil(this.destroy$)).subscribe({
        next: (resp: any) => {
          this.selectedRows = []
          resp.data.creationTimestamp = new Date(resp.data.creationTimestamp)
          this.formDialogData = structuredClone(resp.data)
          this.formDialogVisible = true;
          this.dataLoading$.next(false)
        },
        error: () => {
          this.dataLoading$.next(false)
        }
      })
    } else {
      this.formDialogData = this.DEFAULT_DATA_FN()
      this.formDialogVisible = true
    }
  }

  hideFormDialog() {
    this.formDialogVisible = false;
    this.formDialogData = this.DEFAULT_DATA_FN();
  }

  // saveRecord() {
  //   this.dataLoading$.next(true)
  //   this.http.post(this.ENV.apiUrl + (this.isNewRecordDialog ? this.CREATE_RECORD_ENDPOINT : this.EDIT_RECORD_ENDPOINT), this.cleanDtoToSend(this.formDialogData)).pipe(takeUntil(this.destroy$)).subscribe({
  //     next: (resp: any) => {
  //       if (resp.code == 201) {
  //         this.notifications.showNotification({
  //           severity: "info",
  //           title: "noModifications",
  //           addToHistory: false
  //         })
  //       } else {
  //         this.refreshData()
  //         this.notifications.showNotification({
  //           severity: "success",
  //           title: "operationComplete",
  //           detail: this.DATA_SAVED_MESSAGE,
  //           detailParams: {
  //             count: 1
  //           },
  //           translateDetailsParams: false,
  //           addToHistory: false
  //         })
  //       }
  //       this.hideFormDialog()
  //       this.dataLoading$.next(false)
  //     },
  //     error: (err) => {
  //       this.dataLoading$.next(false)
  //     }
  //   })
  // }

  deleteRecord() {
    this.confirmDialogs.showDeleteConfirmAuto({
      endpoint: this.DELETE_DATA_ENDPOINT,
      selection: [this.formDialogData],
      message: this.DELETE_CONFIRMATION_MESSAGE,
      idKey: this.KEY_NAME,
      onAcceptClick: () => {
        this.dataLoading$.next(true)
      },
      successCallback: () => {
        this.refreshData()
        this.hideFormDialog()
      },
      completeCallback: () => {
        this.dataLoading$.next(false)
      }
    })
  }

  deleteRecords() {
    this.confirmDialogs.showDeleteConfirmAuto({
      endpoint: this.DELETE_DATA_ENDPOINT,
      selection: this.selectedRows,
      message: this.DELETE_CONFIRMATION_MESSAGE,
      idKey: this.KEY_NAME,
      onAcceptClick: () => {
        this.dataLoading$.next(true)
      },
      successCallback: () => {
        this.refreshData()
        this.selectedRows = []
      },
      completeCallback: () => {
        this.dataLoading$.next(false)
      }
    })
  }

  printReportContext() {
    const context = this.formDialogData?.context;
    const reportId = this.formDialogData?.reportId ?? 'sconosciuto';

    console.log(
      `%cContesto segnalazione #${reportId}`,
      'background: #1976d2; color: white; font-weight: bold; padding: 4px 12px; border-radius: 4px;'
    );

    try {
      console.log(JSON.parse(context as any ?? '"Nessun contesto"'));
    } catch (e) {
      console.warn('%cContesto non valido o non in formato JSON:', 'color: orange; font-weight: bold;');
      console.warn(context);
    }

    this.notifications.showNotification({
      severity: 'info',
      title: 'contextPrintedInConsole'
    });
  }


  //? ALTRO
  translateObjectValues<T>(obj: T, keyToTranslate: string): T {
    if (Array.isArray(obj)) {
      return obj.map(item => this.translateObjectValues(item, keyToTranslate)) as T;
    }

    if (obj && typeof obj === 'object') {
      const result = { ...obj };
      for (const [key, value] of Object.entries(result)) {
        if (key === keyToTranslate && typeof value === 'string') {
          (result as { [key: string]: any })[key] = this.transloco.translate(value);
        } else if (typeof value === 'object') {
          (result as { [key: string]: any })[key] = this.translateObjectValues(value, keyToTranslate);
        }
      }
      return result as T;
    }
    return obj;
  }

  onFiltersDialogHide() {
    this.dataFilters.values = structuredClone(this.dataFilters.applied)
  }

  getCellData(col: Column, data: any) {
    let pipe = col.pipe

    let value = getPathKey(data, col.nestedField ?? col.field, col.defaultValue ?? '<span class="null-chip">null</span>')

    if (pipe) {
      if (!Array.isArray(pipe)) {
        pipe = [pipe, {}]
      }
      let pipeconfig = pipe[1]
      pipeconfig = this.replaceObjectValues(pipeconfig, getPathKey(data, col.nestedField ?? col.field, col.defaultValue), data, value)
      switch (pipe[0]) {
        case "date":
          value = this.translocoDate.transform(value, pipeconfig, pipeconfig.locale);
          break

        case "currency":
          value = this.translocoCurrency.transform(value, pipeconfig.display, pipeconfig.numberFormatOptions, pipeconfig.currencyCode, pipeconfig.locale);
          break

        case "percent":
          value = this.translocoPercent.transform(value, pipeconfig, pipeconfig.locale);
          break

        case 'decimal':
          value = this.translocoDecimal.transform(value, pipeconfig, pipeconfig.locale);
          break

        case 'translate':
          if ("prefix" in pipeconfig) {
            value = pipeconfig.prefix + value
            delete pipeconfig.prefix
          }
          value = this.transloco.translate(String(value), pipeconfig)
          break

        case 'duration':
          value = this.durationPipe.transform(value, pipeconfig);
          break

        case "custom":
          value = pipe[1](value)
          break

        default:
          return value;
      }
    }
    return value;
  }

  cleanDtoToSend(dto: any) {
    let res = structuredClone(dto)
    for (const key in res) {
      if (res[key] === undefined || res[key] === null || res[key] === "") {
        delete res[key]
      }
    }
    return res
  }

  parseWrapperInputs(col: Column, value: [any, any]) {
    let inputs = col.wrapperConfig;
    if (inputs) {
      inputs = structuredClone(inputs);
      const rowData = value[0];
      const currentColumnValue = value[1];
      const currentColumnValueOriginal = getPathKey(rowData, col.nestedField ?? col.field, col.defaultValue ?? '<span class="null-chip">null</span>');
      return this.replaceObjectValues(inputs, currentColumnValue, rowData, currentColumnValueOriginal)
    } else {
      return {};
    }
  }

  replaceObjectValues(obj: { [key: string]: any }, currentColumnValue: any, rowData: any, currentColumnValueOriginal: any) {
    if (typeof obj === "object" && obj !== null) {
      let objectCopy = structuredClone(obj);

      for (let key in objectCopy) {
        if (typeof objectCopy[key] === 'string') {
          // Replace {{columnField}} with the corresponding value from rowData
          objectCopy[key] = objectCopy[key].replace(/{{([\w.]+)}}/g, (match: any, path: any) => {
            if (path === 'value') {
              return currentColumnValue;
            }
            return getPathKey(rowData, path, match);
          });
        } else if (typeof objectCopy[key] === 'object') {
          // iterate thru key-val
          for (let subKey in objectCopy[key]) {
            if (typeof objectCopy[key][subKey] === 'string') {
              // Replace {{columnField}} with the corresponding value from rowData
              objectCopy[key][subKey] = objectCopy[key][subKey].replace(/{{([\w.]+)}}/g, (match: any, path: any) => {
                let res = currentColumnValue
                if (path !== 'value') {
                  res = getPathKey(rowData, path, match);
                }
                if (subKey == "--state-color" && res == "base") {
                  res = "var(--p-surface-100)"
                }
                if (subKey == "--state-color-text") {
                  res = res == "base" ? "var(--p-text-color)" : "var(--p-primary-color-text)"
                }
                return res
              });
            }
          }
        }

        const match = key.match(/^(.+)_if_(.+)_(.+)$/);
        if (match) {
          const baseKey = match[1];
          const compareType = match[2];
          const compareValue = match[3];

          let valueToCompare;
          if (compareType === 'val') {
            valueToCompare = currentColumnValueOriginal;
          } else {
            valueToCompare = getPathKey(rowData, compareType, undefined);
          }

          if (String(valueToCompare) === compareValue) {
            if (typeof objectCopy[key] == "string") {
              objectCopy[baseKey] = objectCopy[key].startsWith("%+t%") ?
                this.transloco.translate(objectCopy[key].replace("%+t%", "")) :
                objectCopy[key].replace("%+t%", "");
            } else {
              objectCopy[baseKey] = objectCopy[key]
            }

          }
          delete objectCopy[key];
        }
      }
      return objectCopy;
    }
    return obj
  }
}
